import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/AuthService';

export default function TestAuth() {
  const { login: signIn, signUp, isLoading } = useAuth();
  const [email, setEmail] = useState('test@example.com');
  const [password, setPassword] = useState('wrongpassword');
  const [firstName, setFirstName] = useState('Test');
  const [lastName, setLastName] = useState('User');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSignIn = async () => {
    setError('');
    setSuccess('');
    
    try {
      console.log('🔄 Testing signIn with:', { email, password });
      await signIn(email, password);
      setSuccess('Sign in successful!');
    } catch (error) {
      console.error('❌ Sign in error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      setError(`Sign in error: ${errorMessage}`);
    }
  };

  const handleSignUp = async () => {
    setError('');
    setSuccess('');
    
    try {
      console.log('🔄 Testing signUp with:', { email, password, firstName, lastName });
      await signUp(email, password, firstName, lastName);
      setSuccess('Sign up successful!');
    } catch (error) {
      console.error('❌ Sign up error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
      setError(`Sign up error: ${errorMessage}`);
    }
  };

  const testDirectAPI = async () => {
    setError('');
    setSuccess('');
    
    try {
      console.log('🔄 Testing direct API call...');
      const response = await fetch('/api/auth/sign-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        const error = await response.json();
        console.log('📡 Error response:', error);
        const errorMessage = error.message || error.error || error.code || 'API call failed';
        setError(`Direct API error: ${errorMessage}`);
        return;
      }

      const data = await response.json();
      console.log('📡 Success response:', data);
      setSuccess('Direct API call successful!');
    } catch (error) {
      console.error('❌ Direct API error:', error);
      setError(`Direct API network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-4">
        <h1 className="text-2xl font-bold text-center">Test Auth Error Handling</h1>
        
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-2">
          <Input
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Button 
            onClick={handleSignIn} 
            disabled={isLoading}
            className="w-full"
          >
            Test Sign In (Should Show Error)
          </Button>
          
          <Button 
            onClick={handleSignUp} 
            disabled={isLoading}
            className="w-full"
            variant="outline"
          >
            Test Sign Up (Should Show Error)
          </Button>
          
          <Button 
            onClick={testDirectAPI} 
            disabled={isLoading}
            className="w-full"
            variant="secondary"
          >
            Test Direct API Call
          </Button>
          
          <Button 
            onClick={() => {
              authService.clearAllAuthData();
              setError('');
              setSuccess('All auth data cleared!');
            }} 
            className="w-full"
            variant="destructive"
          >
            Clear All Auth Data
          </Button>
        </div>
        
        <div className="text-sm text-muted-foreground">
          <p>Open browser console to see detailed logs</p>
          <p>Expected: Error messages should appear above</p>
        </div>
      </div>
    </div>
  );
}